import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn, ShieldCheck, KeyRound, Info, Eye, EyeOff, Mail, FileText, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { Brand } from '@/components/ui/Brand';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  // Password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);

  // Forgot-password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Demo login card state
  const [demoOpen, setDemoOpen] = useState(false);

  const DEMO_ACCOUNTS = [
    { label: 'Admin',                    email: 'admin@demo.school',       password: 'Admin123!',   tone: 'indigo' },
    { label: 'HOD (Mr Tan Wei Ming)',    email: 'hod@demo.school',         password: 'Hod123!',     tone: 'emerald' },
    { label: 'Class Teacher (Mrs Lim)',  email: 'teacher@demo.school',     password: 'Teacher123!', tone: 'amber' },
    { label: 'Part-time Teacher (Ms Tan PS)', email: 'parttime@demo.school',    password: 'Part123!',    tone: 'rose' },
    { label: 'Subject Head (Mrs Wong)',  email: 'subjecthead@demo.school', password: 'Head123!',    tone: 'orange' },
    { label: 'Exam Coordinator (Ms Farah)', email: 'examco@demo.school',   password: 'Examco123!',  tone: 'sky' },
  ];

  const fillDemo = (acct) => {
    setEmail(acct.email);
    setPassword(acct.password);
    toast.success(`Filled — ${acct.label}. Click Sign In to continue.`);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (_) {
      setForgotSent(true); // Always show same message (anti-enumeration)
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotDialog = () => {
    setForgotOpen(false);
    setTimeout(() => { setForgotEmail(''); setForgotSent(false); }, 200);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });

      // Check if MFA is required
      if (response.data.mfa_required) {
        setMfaRequired(true);
        setLoading(false);
        return;
      }

      // Check if password change is required
      if (response.data.must_change_password) {
        setTempToken(response.data.token);
        setTempUser(response.data.user);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }

      login(response.data.token, response.data.user);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/mfa-login`, {
        email, password, mfa_code: mfaCode
      });

      if (response.data.must_change_password) {
        setTempToken(response.data.token);
        setTempUser(response.data.user);
        setMustChangePassword(true);
        setMfaRequired(false);
        setLoading(false);
        return;
      }

      login(response.data.token, response.data.user);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: password,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${tempToken}` } });

      login(tempToken, tempUser);
      toast.success('Password changed successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    // Password Change Form
    if (mustChangePassword) {
      return (
        <form onSubmit={handlePasswordChange} className="space-y-5">
          <div className="text-center mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <KeyRound className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Password Change Required</h2>
            <p className="text-xs text-slate-500 mt-1">For security (IM8 AC-6), you must set a new password before continuing.</p>
          </div>
          <div>
            <Label className="text-slate-700 font-medium">New Password</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 8 characters" required className="mt-1.5" data-testid="new-password-input" />
            <div className="mt-2 space-y-0.5">
              {[
                { test: newPassword.length >= 8, label: 'At least 8 characters' },
                { test: /[A-Z]/.test(newPassword), label: '1 uppercase letter' },
                { test: /[a-z]/.test(newPassword), label: '1 lowercase letter' },
                { test: /[0-9]/.test(newPassword), label: '1 number' },
                { test: /[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/.test(newPassword), label: '1 special character' },
              ].map(r => (
                <p key={r.label} className={`text-[11px] ${r.test ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {r.test ? '\u2713' : '\u2022'} {r.label}
                </p>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-slate-700 font-medium">Confirm New Password</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password" required className="mt-1.5" data-testid="confirm-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-900 hover:bg-blue-800 text-white" data-testid="change-password-button">
            {loading ? 'Saving...' : 'Set New Password & Continue'}
          </Button>
        </form>
      );
    }

    // MFA Verification Form
    if (mfaRequired) {
      return (
        <form onSubmit={handleMfaVerify} className="space-y-5">
          <div className="text-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Two-Factor Authentication</h2>
            <p className="text-xs text-slate-500 mt-1">Enter the 6-digit code from your authenticator app.</p>
          </div>
          <div>
            <Label className="text-slate-700 font-medium">Verification Code</Label>
            <Input type="text" inputMode="numeric" maxLength={6} value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000" required className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono"
              data-testid="mfa-code-input" autoFocus />
          </div>
          <Button type="submit" disabled={loading || mfaCode.length !== 6}
            className="w-full bg-blue-900 hover:bg-blue-800 text-white" data-testid="mfa-verify-button">
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </Button>
          <button type="button" onClick={() => { setMfaRequired(false); setMfaCode(''); }}
            className="w-full text-xs text-slate-500 hover:text-slate-700 mt-2">Back to login</button>
        </form>
      );
    }

    // Standard Login Form
    return (
      <form onSubmit={handleLogin} className="space-y-6">
        <div>
          <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@school.edu" required className="mt-1.5 focus:ring-2 focus:ring-blue-200"
            data-testid="login-email-input" />
        </div>
        <div>
          <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
          <div className="relative mt-1.5">
            <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password" required className="pr-10 focus:ring-2 focus:ring-blue-200"
              data-testid="login-password-input" />
            <button type="button"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 rounded"
              data-testid="login-toggle-password-visibility">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading}
          className="w-full bg-blue-900 hover:bg-blue-800 text-white font-medium transition-all duration-200"
          data-testid="login-submit-button">
          {loading ? 'Signing in...' : (<><LogIn className="mr-2 h-4 w-4" />Sign In</>)}
        </Button>
        <div className="text-center">
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            className="text-xs text-slate-500 hover:text-blue-700 hover:underline"
            data-testid="forgot-password-link"
          >
            Forgot your password?
          </button>
        </div>

        {/* Try a Demo Login card */}
        <div className="pt-4 border-t border-slate-100" data-testid="demo-login-section">
          <button
            type="button"
            onClick={() => setDemoOpen(v => !v)}
            className="w-full flex items-center justify-between px-1 py-1.5 text-xs font-black uppercase tracking-[0.15em] text-slate-500 hover:text-slate-900 transition-colors"
            data-testid="demo-login-toggle"
          >
            <span className="flex items-center gap-1.5">
              {demoOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5 rotate-180" />}
              Try a Demo Login
            </span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${demoOpen ? 'rotate-180' : ''}`} />
          </button>

          {demoOpen && (
            <div className="mt-3 space-y-2" data-testid="demo-login-list">
              {DEMO_ACCOUNTS.map((acct) => {
                const toneMap = {
                  indigo:  { bg: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200',   title: 'text-indigo-900',  mono: 'text-indigo-700/80' },
                  emerald: { bg: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200', title: 'text-emerald-900', mono: 'text-emerald-700/80' },
                  amber:   { bg: 'bg-amber-50 hover:bg-amber-100 border-amber-200',     title: 'text-amber-900',   mono: 'text-amber-700/80' },
                  rose:    { bg: 'bg-rose-50 hover:bg-rose-100 border-rose-200',       title: 'text-rose-900',    mono: 'text-rose-700/80' },
                  orange:  { bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200', title: 'text-orange-900',  mono: 'text-orange-700/80' },
                  sky:     { bg: 'bg-sky-50 hover:bg-sky-100 border-sky-200',           title: 'text-sky-900',     mono: 'text-sky-700/80' },
                }[acct.tone];
                return (
                  <button
                    key={acct.email}
                    type="button"
                    onClick={() => fillDemo(acct)}
                    className={`w-full text-left rounded-lg border ${toneMap.bg} px-3.5 py-2.5 transition-colors`}
                    data-testid={`demo-login-${acct.email.split('@')[0]}`}
                  >
                    <p className={`text-sm font-bold ${toneMap.title} leading-tight`}>{acct.label}</p>
                    <p className={`text-[11px] font-mono mt-0.5 ${toneMap.mono}`}>
                      {acct.email} · {acct.password}
                    </p>
                  </button>
                );
              })}
              <p className="text-[10px] text-slate-400 pt-1">
                Click any card to auto-fill, then press <span className="font-bold text-slate-600">Sign In</span>. Demo accounts share the same seeded school.
              </p>
            </div>
          )}
        </div>
      </form>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1654783865377-f059d186c8e4?auto=format&fit=crop&w=2400&q=80)',
        backgroundSize: 'cover', backgroundPosition: 'center'
      }}>
      <TooltipProvider delayDuration={200}>
        <div className="fixed top-0 left-0 right-0 bg-slate-800 text-center py-1 px-2 z-50" data-testid="data-classification-banner">
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-[10px] sm:text-xs font-semibold tracking-widest text-slate-300 uppercase cursor-help inline-flex items-center gap-1.5">
                Official (Closed) &mdash; Unclassified
                <Info className="h-3 w-3 text-slate-400" />
              </p>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm text-left bg-slate-900 border-slate-700 text-slate-200 p-4" data-testid="classification-tooltip">
              <p className="text-xs font-semibold text-white mb-2">Data Classification: OFFICIAL (CLOSED)</p>
              <p className="text-[11px] leading-relaxed mb-2 text-slate-300">This system is classified under Singapore Government IM8 standards.</p>
              <p className="text-[11px] font-medium text-emerald-400 mb-1">Permitted data:</p>
              <p className="text-[11px] text-slate-300 mb-2">Staff names, work emails, timetables, duty assignments, absence reasons</p>
              <p className="text-[11px] font-medium text-red-400 mb-1">Do NOT enter:</p>
              <p className="text-[11px] text-slate-300">NRIC/FIN numbers, medical records, disciplinary records, or classified MOE documents</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
      <div className="absolute inset-0 bg-black/40"></div>
      <div className="relative z-10 w-full max-w-md p-8">
        <div className="bg-white rounded-md shadow-lg p-8 border border-slate-200">
          {!mfaRequired && !mustChangePassword && (
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold tracking-tight mb-2"><Brand /><span className="text-slate-900 ml-2">Management System</span></h1>
              <p className="text-sm text-slate-500">Relief Teacher & Exam Management Tool</p>
            </div>
          )}
          {renderForm()}
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={(open) => { if (!open) closeForgotDialog(); else setForgotOpen(true); }}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Reset your password
            </DialogTitle>
            <DialogDescription>
              {forgotSent
                ? "If an account with that email exists, we've sent a reset link. The link expires in 15 minutes."
                : "Enter your school email. We'll send you a secure reset link."}
            </DialogDescription>
          </DialogHeader>
          {!forgotSent ? (
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="forgot-email" className="text-slate-700 font-medium">Email Address</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="your.email@school.edu"
                  required
                  autoFocus
                  className="mt-1.5"
                  data-testid="forgot-email-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeForgotDialog}>Cancel</Button>
                <Button
                  type="submit"
                  disabled={forgotLoading || !forgotEmail}
                  className="bg-blue-900 hover:bg-blue-800"
                  data-testid="forgot-submit-button"
                >
                  {forgotLoading ? 'Sending...' : 'Send reset link'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4 mt-2">
              <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
                <p className="font-medium mb-1">Check your inbox</p>
                <p className="text-emerald-800/80 text-xs">
                  We've emailed a reset link to <span className="font-medium">{forgotEmail}</span> (if it's registered).
                  Click the link from that email to set a new password. The link expires in 15 minutes.
                </p>
              </div>
              <div className="flex justify-end">
                <Button onClick={closeForgotDialog} className="bg-blue-900 hover:bg-blue-800" data-testid="forgot-close-button">
                  Got it
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
